import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import "@babylonjs/core/Meshes/instancedMesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import type { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Scene } from "@babylonjs/core/scene";

export function createPrototypeGround(
  scene: Scene,
  root: TransformNode,
  groundId: string,
  qualityTier = 0,
): void {
  if (groundId.includes("wood")) {
    const count = qualityTier >= 2 ? 3 : 5;
    const material = makeMaterial(scene, "wood", "#9a6843");
    const plank = MeshBuilder.CreateBox("wood-source", { width: 0.72, height: 0.07, depth: 2.4 }, scene);
    plank.position.set(-((count - 1) * 0.76) / 2, 0.08, 0);
    attach(plank, root, material);
    for (let index = 1; index < count; index += 1) {
      const instance = plank.createInstance(`wood-${index}`);
      instance.position.set(plank.position.x + index * 0.76, 0.08, 0);
      attachInstance(instance, root);
    }
  } else if (groundId.includes("glass")) {
    const material = makeMaterial(scene, "glass", "#86dfe8", 0.48);
    material.specularColor = Color3.White();
    const glass = MeshBuilder.CreateBox("glass-ground", { width: 3.8, height: 0.08, depth: 2.5 }, scene);
    glass.position.y = 0.08;
    attach(glass, root, material);
  } else if (groundId.includes("rock")) {
    const count = qualityTier >= 2 ? 4 : qualityTier === 1 ? 6 : 9;
    const material = makeMaterial(scene, "rocks", "#747d86");
    const sources = [0, 1].map((type) => {
      const source = MeshBuilder.CreatePolyhedron(`rock-source-${type}`, { type, size: 0.38 }, scene);
      source.isVisible = false;
      attach(source, root, material);
      return source;
    });
    for (let index = 0; index < count; index += 1) {
      const source = sources[index % sources.length];
      if (!source) continue;
      const rock = source.createInstance(`rock-${index}`);
      rock.isVisible = true;
      rock.position.set((index % 3 - 1) * 0.95, 0.28, (Math.floor(index / 3) - 1) * 0.7);
      rock.rotation.set(index * 0.17, index * 0.38, 0);
      rock.scaling.setAll(0.9 + (index % 3) * 0.15);
      attachInstance(rock, root);
    }
  } else {
    const count = qualityTier >= 2 ? 6 : qualityTier === 1 ? 10 : 18;
    const material = makeMaterial(scene, "leaves", "#b98235");
    const source = MeshBuilder.CreateBox("leaf-source", { width: 0.28, height: 0.025, depth: 0.13 }, scene);
    source.isVisible = false;
    attach(source, root, material);
    for (let index = 0; index < count; index += 1) {
      const leaf = source.createInstance(`leaf-${index}`);
      leaf.isVisible = true;
      leaf.position.set(((index * 37) % 17) / 5 - 1.6, 0.06, ((index * 23) % 13) / 5 - 1.15);
      leaf.rotation.y = index * 0.91;
      attachInstance(leaf, root);
    }
  }
}

export function createPrototypeEnvironment(
  scene: Scene,
  root: TransformNode,
  environmentId: string,
  qualityTier = 0,
): void {
  if (environmentId.includes("park")) {
    const treeCount = qualityTier >= 2 ? 1 : 2;
    const trunk = makeMaterial(scene, "trunk", "#72513b");
    const crown = makeMaterial(scene, "crown", "#4c875c");
    const stemSource = MeshBuilder.CreateCylinder("tree-stem-source", { height: 1.8, diameter: 0.28, tessellation: 8 }, scene);
    const crownSource = MeshBuilder.CreatePolyhedron("tree-crown-source", { type: 1, size: 0.9 }, scene);
    stemSource.isVisible = false;
    crownSource.isVisible = false;
    attach(stemSource, root, trunk);
    attach(crownSource, root, crown);
    for (let index = 0; index < treeCount; index += 1) {
      const x = treeCount === 1 ? 1.35 : [-1.45, 1.45][index] ?? 0;
      const stem = stemSource.createInstance(`tree-stem-${index}`);
      stem.isVisible = true;
      stem.position.set(x, 0.9, 0.65);
      attachInstance(stem, root);
      const leaves = crownSource.createInstance(`tree-crown-${index}`);
      leaves.isVisible = true;
      leaves.position.set(x, 2.02, 0.65);
      attachInstance(leaves, root);
    }
    const bench = MeshBuilder.CreateBox("park-bench", { width: 1.8, height: 0.16, depth: 0.5 }, scene);
    bench.position.set(0, 0.58, 0.85);
    attach(bench, root, trunk);
  } else {
    const room = makeMaterial(scene, "room-outline", "#a7b2bd");
    const columnSource = MeshBuilder.CreateBox("room-column-source", { width: 0.22, height: 2.5, depth: 0.22 }, scene);
    columnSource.position.set(-1.55, 1.25, 0.75);
    attach(columnSource, root, room);
    const secondColumn = columnSource.createInstance("room-column-1");
    secondColumn.position.set(1.55, 1.25, 0.75);
    attachInstance(secondColumn, root);
    const table = MeshBuilder.CreateBox("room-table", { width: 1.7, height: 0.12, depth: 0.85 }, scene);
    table.position.set(0, 0.82, 0.65);
    attach(table, root, room);
    if (qualityTier < 3) {
      const legSource = MeshBuilder.CreateBox("room-table-leg-source", { width: 0.12, height: 0.8, depth: 0.12 }, scene);
      legSource.position.set(-0.65, 0.4, 0.65);
      attach(legSource, root, room);
      const secondLeg = legSource.createInstance("room-table-leg-1");
      secondLeg.position.set(0.65, 0.4, 0.65);
      attachInstance(secondLeg, root);
    }
  }
}

function makeMaterial(scene: Scene, name: string, color: string, alpha = 1): StandardMaterial {
  const material = new StandardMaterial(`${name}-material`, scene);
  material.diffuseColor = Color3.FromHexString(color);
  material.roughness = 0.86;
  material.alpha = alpha;
  return material;
}

function attach(mesh: Mesh, root: TransformNode, material: StandardMaterial): void {
  mesh.parent = root;
  mesh.material = material;
  mesh.isPickable = false;
}

function attachInstance(mesh: AbstractMesh, root: TransformNode): void {
  mesh.parent = root;
  mesh.isPickable = false;
}
